import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Card, CardBody } from "reactstrap";
import TableContainer from "Components/Common/TableContainer";
import CustomButton from "Components/Custom/CustomButton";
import ActionMenu from "Components/Common/ActionMenu";
import { useAppDispatch } from "Components/Hooks/hooks";
import { deleteCategory, getCategories } from "slices/thunks";
import { createSelector } from "reselect";
import { selectCategoriesState } from "slices/categories/reducer";
import { useSelector } from "react-redux";
import DeleteItemModal from "Components/Modals/DeleteItemModal";

interface CategoryTableProps {
  onAddCategory: () => void;
  onEditCategory: (cat: any) => void;
}

const CategoriesTable = ({
  onAddCategory,
  onEditCategory,
}: CategoryTableProps) => {
  const dispatch = useAppDispatch();
  const [deleteCatId, setDeleteCatId] = useState<number | null>(null);

  const categorySelector = createSelector(selectCategoriesState, (state) => ({
    categories: state.categories,
    isLoading: state.status.fetch,
    error: state.status.fetch,
    meta: state.pagination,
  }));

  const { categories, isLoading, meta } = useSelector(categorySelector);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    const params = searchTerm ? { search: searchTerm } : { page: currentPage };
    dispatch(getCategories(params));
  }, [currentPage, dispatch, searchTerm]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "CATEGORY",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {cell.getValue()}
            </span>
          );
        },
      },
      {
        header: "SUBCATEGORIES",
        accessorKey: "subcategories_count",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "ITEMS",
        accessorKey: "items_count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "ACTION",
        enableSorting: false,
        cell: (cell: any) => {
          return (
            <ActionMenu
              onEdit={() => onEditCategory(cell?.row?.original)}
              onDelete={() => setDeleteCatId(cell.row.original.id)}
            />
          );
        },
      },
    ],
    [onEditCategory]
  );

  return (
    <React.Fragment>
      <DeleteItemModal
        show={deleteCatId !== null}
        onCloseClick={() => setDeleteCatId(null)}
        onDeleteClick={() => {
          if (deleteCatId) {
            dispatch(deleteCategory(deleteCatId));
          }
          setDeleteCatId(null);
        }}
      />
      <Card id="membersTable" className="br-10 bg-white">
        <CardBody className="pt-0">
          <TableContainer
            columns={columns}
            data={categories}
            isGlobalFilter={true}
            divClass="table-responsive table-card mb-2 bg-white"
            tableClass="align-middle table-nowrap table-borderless category-table"
            thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
            theadClass="table-light"
            SearchPlaceholder="Search category"
            onSearch={handleSearch}
            onPageChange={handlePageChange}
            customPageSize={meta?.per_page}
            totalPages={meta?.last_page}
            currentPage={meta?.current_page}
            fromPage={meta?.from}
            toPage={meta?.to}
            totalItems={meta?.total}
            searchAlign="left"
            rightContent={
              <CustomButton
                text="New Category"
                leftIcon={<i className="ri-add-line me-1 align-bottom fs-5" />}
                className="bg-primary br-7"
                onClick={onAddCategory}
              />
            }
            isInitialLoading={isLoading === "loading" && !categories?.length}
            isPaginationLoading={
              isLoading === "loading" && categories?.length > 0
            }
          />
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default CategoriesTable;
